import { Timer } from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────────────

interface AvgReviewTimeCardProps {
  avgMinutes: number | null;
  overrideRatePct: number | null;
  escalationRatePct: number | null;
  loading?: boolean;
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

function CardSkeleton() {
  return (
    <div role="status" aria-label="Loading review metrics" className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex justify-between">
          <div className="h-4 w-28 animate-pulse rounded bg-muted" aria-hidden="true" />
          <div className="h-4 w-12 animate-pulse rounded bg-muted" aria-hidden="true" />
        </div>
      ))}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatMinutes(mins: number): string {
  if (mins < 60) return `${Math.round(mins)}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${m}m`;
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * KPI metrics card for Team Monitoring tab.
 * Shows average review time, override rate, and escalation rate.
 *
 * FR-083: Team performance metrics.
 */
export function AvgReviewTimeCard({
  avgMinutes,
  overrideRatePct,
  escalationRatePct,
  loading,
}: AvgReviewTimeCardProps) {
  return (
    <section
      className="rounded-lg border border-border bg-card p-4 space-y-3"
      aria-label="Review performance metrics"
    >
      <div className="flex items-center gap-2">
        <Timer className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-foreground">Review Performance</h3>
      </div>

      {loading ? (
        <CardSkeleton />
      ) : (
        <dl className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <dt className="text-muted-foreground">Avg review time</dt>
            <dd className="font-semibold tabular-nums">
              {avgMinutes != null ? formatMinutes(avgMinutes) : '—'}
            </dd>
          </div>
          <div className="flex items-center justify-between text-sm">
            <dt className="text-muted-foreground">Override rate</dt>
            <dd className="font-semibold tabular-nums">
              {overrideRatePct != null ? `${overrideRatePct.toFixed(1)}%` : '—'}
            </dd>
          </div>
          <div className="flex items-center justify-between text-sm">
            <dt className="text-muted-foreground">Escalation rate</dt>
            <dd className="font-semibold tabular-nums">
              {escalationRatePct != null ? `${escalationRatePct.toFixed(1)}%` : '—'}
            </dd>
          </div>
        </dl>
      )}
    </section>
  );
}
